// ─── AuthGuard ──────────────────────────────────────────────────────────────
// Wrap any page that needs someone to be signed in:
//
//   <AuthGuard><Dashboard /></AuthGuard>               customer pages
//   <AuthGuard area="qc"><QcHome /></AuthGuard>         QC staff pages
//
// If the right token is missing or has expired, the person is sent to the
// matching sign-in page. The address they were trying to open travels along
// as "next", so they land back on it after signing in.
//
// This only improves the experience. The real protection is on the server,
// which checks the token on every request (see SecurityConfig.java).

import { Navigate, useLocation } from 'react-router-dom';
import { getToken, isTokenValid, clearToken } from './token';

const SIGNIN_PATHS = {
  customer: '/signin',
  qc:       '/qc/login',
};

export default function AuthGuard({ area = 'customer', children }) {
  const location = useLocation();
  const token = getToken(area);

  if (isTokenValid(token)) return children;

  // A token that exists but no longer works = expired. No token = never signed in.
  const reason = token ? 'expired' : 'signin';
  clearToken(area);
  const next = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`${SIGNIN_PATHS[area]}?reason=${reason}&next=${next}`} replace />;
}

// Only allow "next" to point inside our own site. Without this check, a link
// like /signin?next=https://bad-site.com could send people somewhere else.
export function safeNextPath(next, fallback) {
  if (next && next.startsWith('/') && !next.startsWith('//')) return next;
  return fallback;
}
